import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);


if (!supabaseUrl || !supabaseAnonKey) {
  // eslint-disable-next-line no-console
  console.warn('Supabase env vars missing. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

// Use default storage (localStorage) so session is restored on reload and Supabase can auto-refresh.
const supabaseClient =
  isSupabaseConfigured ? createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    }
  }) : null;

export const supabase = supabaseClient;

// No-op stub so callers can still import cookieSession without breaking.
export const cookieSession = {
  saveSessionToCookie: async () => {},
  getSessionFromCookie: async () => null,
  clearSessionCookie: async () => {},
  getSessionFromCookieWithTimeout: () => Promise.reject(new Error('GET_COOKIE_TIMEOUT'))
};

const ensureSupabase = () => {
  if (!supabaseClient) {
    throw new Error('Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
  }
  return supabaseClient;
};

// ---------------------------------------------------------------------------
// Synchronous bootstrap hint — read localStorage before Supabase initialises
// ---------------------------------------------------------------------------

export const AUTH_HINT_KEY = 'grotn_auth_hint';
export const AUTH_USER_PREVIEW_KEY = 'grotn_user_preview';

const parseStoredJson = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  try { return JSON.parse(raw); } catch { return null; }
};

// Reads Supabase's own persisted session from localStorage.
// Returns null if the key is missing OR if the access token is already expired
// (so we never show a "logged in" hint for a dead token).
const readSupabaseStoredSession = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const sessionKey = Object.keys(window.localStorage)
    .find((k) => k.startsWith('sb-') && k.endsWith('-auth-token'));
  if (!sessionKey) return null;
  const parsed = parseStoredJson(window.localStorage.getItem(sessionKey));
  if (!parsed) return null;

  let session = null;
  if (parsed?.currentSession) session = parsed.currentSession;
  else if (parsed?.access_token) session = parsed;
  else if (Array.isArray(parsed) && parsed[0]?.currentSession) session = parsed[0].currentSession;

  if (!session?.access_token) return null;

  // Treat an expired access token as no session so the navbar shows
  // "Get started" instantly instead of flashing the avatar then clearing it.
  const expiresAt = session.expires_at ? session.expires_at * 1000 : null;
  if (expiresAt && expiresAt < Date.now()) return null;

  return session;
};

export const persistLocalAuthHint = (userPreview) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(AUTH_HINT_KEY, 'logged_in');
  if (userPreview) {
    window.localStorage.setItem(AUTH_USER_PREVIEW_KEY, JSON.stringify(userPreview));
  }
};

export const clearLocalAuthHint = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.removeItem(AUTH_HINT_KEY);
  window.localStorage.removeItem(AUTH_USER_PREVIEW_KEY);
};

// Called synchronously during the first render (useState initialiser).
// Returns { hasLocalSessionHint: bool, cachedUserPreview: object|null }.
// The ONLY way hasLocalSessionHint is true is if Supabase has a non-expired
// token in localStorage right now — eliminating stale-hint flickers.
export const getLocalAuthBootstrapHint = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { hasLocalSessionHint: false, cachedUserPreview: null };
  }

  const storedSession = readSupabaseStoredSession();

  if (!storedSession) {
    // Clean up our own stale hint keys if the Supabase session is gone/expired.
    if (window.localStorage.getItem(AUTH_HINT_KEY) === 'logged_in') {
      clearLocalAuthHint();
    }
    return { hasLocalSessionHint: false, cachedUserPreview: null };
  }

  // We have a non-expired Supabase session — the user is (at least locally) logged in.
  const cachedPreview = parseStoredJson(window.localStorage.getItem(AUTH_USER_PREVIEW_KEY));
  const fallbackPreview = mapSupabaseUser(storedSession?.user || null);
  return {
    hasLocalSessionHint: true,
    cachedUserPreview: cachedPreview || fallbackPreview || null,
  };
};

// ---------------------------------------------------------------------------

const mapSupabaseUser = (user) => {
  if (!user) return null;
  const metadata = user.user_metadata || {};
  const appMetadata = user.app_metadata || {};
  const email = user.email || '';
  return {
    id: user.id,
    email,
    name: metadata.full_name || metadata.name || email.split('@')[0] || 'User',
    picture: metadata.avatar_url || metadata.picture || null,
    authProvider: appMetadata.provider || 'email',
    createdAt: user.created_at
  };
};

// Session validation utility
export const validateSession = async () => {
  try {
    if (!supabaseClient) {
      return { valid: false, error: 'Supabase not initialized' };
    }

    const { data, error } = await supabaseClient.auth.getSession();
    
    if (error) {
      return { valid: false, error: error.message };
    }

    if (!data?.session) {
      return { valid: false, error: 'No session found' };
    }

    const session = data.session;
    const expiresAt = session.expires_at ? session.expires_at * 1000 : null;
    const now = Date.now();
    
    // Check if token is expired
    if (expiresAt && expiresAt < now) {
      // Token expired, try to refresh
      try {
        const { data: refreshData, error: refreshError } = await supabaseClient.auth.refreshSession();
        if (refreshError || !refreshData?.session) {
          return { valid: false, error: 'Session expired and refresh failed' };
        }
        return { valid: true, session: refreshData.session, refreshed: true };
      } catch (refreshErr) {
        return { valid: false, error: 'Failed to refresh expired session' };
      }
    }

    // Check if token is close to expiry (within 5 minutes)
    const fiveMinutes = 5 * 60 * 1000;
    if (expiresAt && (expiresAt - now) < fiveMinutes) {
      // Token is close to expiry, proactively refresh
      try {
        const { data: refreshData, error: refreshError } = await supabaseClient.auth.refreshSession();
        if (!refreshError && refreshData?.session) {
          return { valid: true, session: refreshData.session, refreshed: true };
        }
        // If refresh fails but token is still valid, return current session
        return { valid: true, session, refreshed: false };
      } catch (refreshErr) {
        // If refresh fails but token is still valid, return current session
        return { valid: true, session, refreshed: false };
      }
    }

    return { valid: true, session, refreshed: false };
  } catch (error) {
    return { valid: false, error: error.message || 'Session validation failed' };
  }
};

// Retry logic for session restoration with exponential backoff
export const restoreSessionWithRetry = async (maxRetries = 3, initialDelay = 1000) => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const validation = await validateSession();
      if (validation.valid) {
        return validation.session;
      }

      // If this is not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        const delay = initialDelay * Math.pow(2, attempt); // Exponential backoff
        console.log(`Session restoration attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    } catch (error) {
      console.error(`Session restoration attempt ${attempt + 1} error:`, error);
      if (attempt < maxRetries) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  console.warn(`Session restoration failed after ${maxRetries + 1} attempts`);
  return null;
};

export const auth = {
  signInWithGoogle: async () => {
    const client = ensureSupabase();
    const { data, error } = await client.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    });
    if (error) throw error;
    return data;
  },

  signInWithEmail: async (email, password) => {
    const client = ensureSupabase();
    const { data, error } = await client.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },

  signUpWithEmail: async (name, email, password) => {
    const client = ensureSupabase();
    const { data, error } = await client.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name
        }
      }
    });
    if (error) throw error;
    return data;
  },

  signOut: async () => {
    const client = ensureSupabase();
    const { error } = await client.auth.signOut();
    if (error) throw error;
  },

  getSession: async () => {
    const client = ensureSupabase();
    const { data, error } = await client.auth.getSession();
    if (error) throw error;
    return data.session;
  },

  onAuthStateChange: (callback) => {
    if (!supabaseClient) {
      return { data: { subscription: { unsubscribe: () => {} } } };
    }
    return supabaseClient.auth.onAuthStateChange(callback);
  },

  toAppUser: mapSupabaseUser,
  validateSession,
  restoreSessionWithRetry
};
